
/**
 * Image Cache Utility
 * 
 * This utility provides a client-side mapping between a stable image path (the base S3 path)
 * and a temporary signed URL. This allows the browser to leverage its native internal cache
 * even when the backend provides "freshly signed" URLs that would otherwise break the cache
 * due to changing signature strings.
 */

interface CacheEntry {
  signedUrl: string;
  expiresAt: number;
}

const CACHE_KEY = 'cc_image_sign_cache';
const DEFAULT_TTL_MINUTES = 30;
const MAX_CACHE_SIZE = 500; // items

/**
 * Extracts the base path from a URL (strips query parameters)
 */
const getBasePath = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.origin + urlObj.pathname;
  } catch (e) {
    // If it's not a full URL (relative path), just strip query params manually
    return url.split('?')[0];
  }
};

/**
 * Reads the cache from storage
 */
const readCache = (): Record<string, CacheEntry> => {
  try {
    const data = localStorage.getItem(CACHE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.warn('Failed to read image cache:', e);
    return {};
  }
};

/**
 * Persists the cache to storage
 */
const writeCache = (cache: Record<string, CacheEntry>) => {
  try {
    // Basic LRU-ish cleanup if cache gets too big
    const keys = Object.keys(cache);
    if (keys.length > MAX_CACHE_SIZE) {
      // Sort by expiresAt and remove the oldest 100
      const sortedKeys = keys.sort((a, b) => cache[a].expiresAt - cache[b].expiresAt);
      for (let i = 0; i < 100; i++) {
        delete cache[sortedKeys[i]];
      }
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e: any) {
    // If storage is full, clear half the cache
    if (e && e.name === 'QuotaExceededError') {
      const allKeys = Object.keys(cache);
      for (let i = 0; i < allKeys.length / 2; i++) {
        delete cache[allKeys[i]];
      }
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      } catch (inner) {
        localStorage.removeItem(CACHE_KEY);
      }
    }
  }
};

/**
 * Checks if a valid signed URL exists in the cache for the given path
 */
export const getCachedSignedUrl = (url: string): string | null => {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return null;
  
  // If it's already a signed URL (has query params), checking cache is useful 
  // to avoid cache busting if we have a different valid signed URL.
  const basePath = getBasePath(url);
  const cache = readCache();
  const entry = cache[basePath];

  if (entry && entry.expiresAt > Date.now()) {
    return entry.signedUrl;
  }

  return null;
};

/**
 * Saves a signed URL to the cache
 */
export const cacheSignedUrl = (url: string, ttlMinutes = DEFAULT_TTL_MINUTES): void => {
  if (!url || !url.includes('?') || url.startsWith('data:') || url.startsWith('blob:')) return;

  const basePath = getBasePath(url);
  const cache = readCache();
  
  cache[basePath] = {
    signedUrl: url,
    expiresAt: Date.now() + ttlMinutes * 60 * 1000
  };

  writeCache(cache);
};

/**
 * Clears expired entries from the cache
 */
export const pruneImageCache = (): void => {
  const cache = readCache();
  const now = Date.now();
  let changed = false;

  for (const key in cache) {
    if (cache[key].expiresAt < now) {
      delete cache[key];
      changed = true;
    }
  }

  if (changed) {
    writeCache(cache);
  }
};
